import { memo, useMemo } from "react";
import { useAppSelector } from "../../store/hooks/hooks";
import { Trans } from "react-i18next";
import Input from "../../components/common/Input";
import DropdownMenu, { type IDDItem } from "../../components/common/DropdownMenu";

interface ToolInputRowProps {
	inputId: string;
	dropdownItems: IDDItem[];
	onInputChange: (inputName: string, value: number | null) => void;
	onSelectUnit: (inputId: string, toUnitId: string) => void;
	disabled?: boolean;
}

export const InputRow: React.FC<ToolInputRowProps> = memo(({
	inputId,
	dropdownItems,
	onInputChange,
	onSelectUnit,
	disabled = false
}) => {
	const input = useAppSelector(state => {
		const well = inputId.includes('well1') ? state.iprCalculator?.well1 : state.iprCalculator?.well2;
		const calculationType = well?.calculationType;

		if (calculationType === 'vertical') {
			return well?.verticalInputs.find(item => item.id === inputId) ?? null;
		}
		if (calculationType === 'horizontal') {
			return well?.horizontalInputs.find(item => item.id === inputId) ?? null;
		}
		if (calculationType === 'jones') {
			return well?.jonesInputs.find(item => item.id === inputId) ?? null;
		}

		return null;
	});

	const showHints = useAppSelector(state => state.settings.showHints);
	const tooltipContent = useMemo(() => {
		if (!showHints) {
			return undefined;
		}

		return (
			<Trans
				ns={'ipr'}
				i18nKey={`form_field_titles.${input?.queryName}`}
				components={{
					sub: <sub />,
					sup: <sup />,
				}}
			/>
		);
	}, [showHints, input?.queryName]);

	const labelContent = useMemo(() => {
		return (
			<Trans
				ns={"ipr"}
				i18nKey={`form_fields.${input?.queryName}`}
				components={{
					sub: <sub />,
					sup: <sup />,
				}}
			/>
		);
	}, [input?.queryName]);

	if (!input) {
		return null;
	}

	const onDDSelectItem = (selectedItemId: string) => {
		onSelectUnit(input.id, selectedItemId);
	}

	const value: number | null = useMemo(() => {
		if (input.currentValue == null)
			return null;

		if (input.currentUnitId != null && input.currentUnitId == input.userUnitId)
			return input.userValue;

		return parseFloat(input.currentValue.toFixed(4));
	}, [input]);

	return (
		<div className="ipr__input-group">
			<label className="ipr__input-label" htmlFor={input.id}>
				{labelContent}:
			</label>
			<Input
				value={value}
				onChange={onInputChange}
				name={input.queryName}
				id={input.id}
				tooltip={tooltipContent}
				disabled={disabled}
				error={input.error}
				errorParams={input.errorParams}
			/>
			{input.unitCategoryId && input.currentUnitId && (
				<DropdownMenu
					inputId={input.id}
					items={dropdownItems}
					selectedItemId={input.currentUnitId}
					onSelect={onDDSelectItem}
					disabled={disabled}
				/>)
			}
		</div>
	);
});

InputRow.displayName = "IPRInputRow";
